import moment from 'moment';

// Function that takes in Patient data object and returns an array of its requiredImmunizations with the due dates populated.
// Due dates are calculated client-side since it's based on the DOB. There are some custom due date calculations.
function populateRequiredImmunizationDueDates(data: Object) {
  const { requiredImmunizations } = data;

  // For specific vaccines in a sequence, set the due date based off when the last one in the sequence was administered.
  // Most of them follow the same date interval, i.e. IMM1 is 6 weeks after IMM0, IMM2 is 4 weeks after IMM1,
  // IMM3 is 4 weeks after IMM2.
  // Two exceptions are IPV is taken together with OPV 3, and HEP A 2 is taken 6 months after Hep A 1.

  // If last administered date not present, fallback to absolute time from date of birth.

  // The final overridden due date will always be based off the latest administered imm in the sequence.
  // e.g. if both OPV 0 and OPV 1 have been administered, the due date of OPV 2 will be based off OPV 1.

  // Map to contain rules such that the key is the administered imm, and it maps to a the imm's it should override,
  // with what values. e.g. the first entry specifies that for an administered OPV 0 imm, set the due date of
  // OPV 1 to be 6 weeks after administer date, OPV 2 to be 10 weeks after, and OPV 3 and IPV to be 14 weeks after.
  const customDateDueRules = {
    'OPV 0': {
      'OPV 1': { weeks: 6 }, 'OPV 2': { weeks: 10 }, 'OPV 3': { weeks: 14 }, IPV: { weeks: 14 },
    },
    'OPV 1': { 'OPV 2': { weeks: 4 }, 'OPV 3': { weeks: 8 }, IPV: { weeks: 8 } },
    'OPV 2': { 'OPV 3': { weeks: 4 }, IPV: { weeks: 4 } },
    'DPT/Hib/Hep B 1': { 'DPT/Hib/Hep B 2': { weeks: 4 }, 'DPT/Hib/Hep B 3': { weeks: 8 } },
    'DPT/Hib/Hep B 2': { 'DPT/Hib/Hep B 3': { weeks: 4 } },
    'PCV 1': { 'PCV 2': { weeks: 4 }, 'PCV 3': { weeks: 8 } },
    'PCV 2': { 'PCV 3': { weeks: 4 } },
    'Rotarix 1': { 'Rotarix 2': { weeks: 4 } },
    'Hep A 1': { 'HEP A 2': { months: 6 } },
    'HPV 1': { 'HPV 2': { months: 6 } },
  };
  // Map to contain derived due date overrides.
  const dateDueOverrides = {};
  requiredImmunizations.forEach((immunization) => {
    if (immunization.immunization in customDateDueRules && immunization.dateAdministered) {
      Object.entries(customDateDueRules[immunization.immunization]).forEach(([immToOverride, duration]) => {
        dateDueOverrides[immToOverride] = moment(immunization.dateAdministered).add(duration);
      });
    }
  });

  return requiredImmunizations.map((immunization) => {
    let dateDue = data.generalInfo.dob;
    if (dateDue) {
      if (immunization.age === 'Birth') {
        dateDue = moment(dateDue);
      } else {
        const [amount, interval] = immunization.age.split(' ');
        dateDue = moment(dateDue).add(amount, interval);
        if (immunization.immunization === 'Albendazole 400mg' && interval === 'years' && parseFloat(amount) > 5) {
          // For Albendazole, after 5th birthday, should be administered on Jan 1 and July 1.
          // e.g. If fifth birthday is 5/29/20, 5.5yo imm due date is 7/1/20, 6yo due date is 1/1/21, etc.
          const adjustedMonth = Math.floor(dateDue.month() / 6) * 6; // 0 for Jan 1-Jun 30, 6 for Jul 1 - Dec 31
          dateDue = dateDue.date(1).month(adjustedMonth);
        }
      }
    }

    // Override based off previous date administered even if DOB not present.
    if (immunization.immunization in dateDueOverrides) {
      dateDue = dateDueOverrides[immunization.immunization];
    }

    return {
      ...immunization,
      dateDue,
    };
  });
}

export default function transformRequiredImmunizations(data: Object) {
  // Populate due dates
  let requiredImmunizations = populateRequiredImmunizationDueDates(data);
  // Front End transformation of 10 year MMR imm -> MR imm, so we don't have to do scary DB updates
  requiredImmunizations = requiredImmunizations.map(
    imm => (imm.immunization === 'MMR' && imm.age === '10 years' ? { ...imm, immunization: 'MR' } : imm),
  );
  return requiredImmunizations;
}



// WEBPACK FOOTER //
// ./client/src/util/immunizationUtil.js